import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  Switch,
  Tooltip,
  TableSortLabel,
  Typography,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  GetApplicationList,
  GetApplicationsModulesList,
  GetApplicationsSubModulesList,
  AddApplicationsSubModules,
  UpdateApplicationsSubModules,
} from "../../../services/admin";
import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";
import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";

const AddSubModules = () => {
  const [applications, setApplications] = useState([]);

  const [modules, setModules] = useState([]);

  const [list, setList] = useState([]);

  const [editId, setEditId] = useState(null);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [globalSearch, setGlobalSearch] = useState("");

  const [showAllFilters, setShowAllFilters] = useState(false);

  const [searchText, setSearchText] = useState({});

  const [order, setOrder] = useState("asc");

  const [orderBy, setOrderBy] = useState("");

  useEffect(() => {
    fetchApplications();

    fetchModules();

    fetchSubModules();
  }, []);

  // =========================================
  // FETCH APPLICATIONS
  // =========================================

  const fetchApplications = async () => {
    try {
      const response = await GetApplicationList();

      const formattedData = (response?.data || []).map((item) => ({
        application_ID: item.application_ID,

        application_Name: item.Application_Name || item.application_Name,

        IsActive: item.isActive,
      }));

      setApplications(formattedData);
    } catch (error) {
      console.log(error);

      toast.error("Failed to load applications");
    }
  };

  // =========================================
  // FETCH MODULES
  // =========================================

  const fetchModules = async () => {
    try {
      const response = await GetApplicationsModulesList();

      const formattedData = (response?.data || []).map((item) => ({
        module_ID: item.module_ID,

        application_ID: item.application_ID,

        module_Name: item.module_Name,

        IsActive: item.isActive,
      }));

      setModules(formattedData);
    } catch (error) {
      console.log(error);

      toast.error("Failed to load modules");
    }
  };

  // =========================================
  // FETCH SUBMODULES
  // =========================================

  const fetchSubModules = async () => {
    try {
      const response = await GetApplicationsSubModulesList();

      const formattedData = (response?.data || []).map((item) => ({
        sub_Module_ID: item.Sub_Module_ID || item.sub_Module_ID,

        module_ID: item.Module_ID || item.module_ID,

        module_Name: item.Module_Name || item.module_Name,

        application_ID: item.Application_ID || item.application_ID,

        application_Name: item.Application_Name || item.application_Name,

        sub_Module_Name: item.Sub_Module_Name || item.sub_Module_Name,

        IsActive: item.IsActive ?? item.isActive,
      }));

      setList(formattedData);
    } catch {
      toast.error("Failed to load sub modules");
    }
  };

  // =========================================
  // VALIDATION
  // =========================================

  const validationSchema = Yup.object({
    application_ID: Yup.number()
      .typeError("Application is required")
      .required("Application is required"),

    module_ID: Yup.number()
      .typeError("Module is required")
      .required("Module is required"),

    sub_Module_Name: Yup.string()
      .trim()
      .required("Sub Module name is required")
      .test(
        "duplicate-submodule",
        "Sub Module already exists",
        function (value) {
          if (!value) return true;

          const duplicate = list.some(
            (item) =>
              item.module_ID === this.parent.module_ID &&
              item.sub_Module_Name?.toLowerCase().trim() ===
                value.toLowerCase().trim() &&
              item.sub_Module_ID !== editId,
          );

          return !duplicate;
        },
      ),
  });

  // =========================================
  // FORMIK
  // =========================================

  const formik = useFormik({
    initialValues: {
      application_ID: "",

      module_ID: "",

      sub_Module_Name: "",

      IsActive: true,
    },

    validationSchema,

    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          Sub_Module_ID: editId || 0,

          Module_ID: values.module_ID,

          Sub_Module_Name: values.sub_Module_Name.trim(),

          IsActive: values.IsActive ? 1 : 0,
        };

        if (editId) {
          await UpdateApplicationsSubModules(payload);

          toast.success("Sub Module updated successfully");
        } else {
          await AddApplicationsSubModules(payload);

          toast.success("Sub Module added successfully");
        }

        fetchSubModules();

        resetForm();

        setEditId(null);
      } catch {
        toast.error("Save failed");
      }
    },
  });
  const filteredModules = modules.filter(
    (x) => String(x.application_ID) === String(formik.values.application_ID),
  );

  // =========================================
  // APPLICATION CHANGE
  // =========================================

  // =========================================
  // EDIT
  // =========================================

  const handleEdit = (item) => {
    setEditId(item.sub_Module_ID);

    formik.setValues({
      application_ID: String(item.application_ID),

      module_ID: String(item.module_ID),

      sub_Module_Name: item.sub_Module_Name,

      IsActive: item.IsActive,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================
  // RESET
  // =========================================

  const resetForm = () => {
    setEditId(null);

    formik.resetForm();
  };

  // =========================================
  // SORT
  // =========================================

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";

    setOrder(isAsc ? "desc" : "asc");

    setOrderBy(property);
  };

  // =========================================
  // RESET FILTERS
  // =========================================

  const handleResetFilters = () => {
    setGlobalSearch("");

    setSearchText({});

    setShowAllFilters(false);

    setPage(0);
  };

  // =========================================
  // FILTER
  // =========================================

  const filteredList = list.filter((item) => {
    const globalMatch = Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(globalSearch.toLowerCase());

    const columnMatch = Object.keys(searchText).every((key) => {
      if (!searchText[key]) return true;

      const value = item[key]?.toString().toLowerCase() || "";

      return value.includes(searchText[key].toLowerCase());
    });

    return globalMatch && columnMatch;
  });

  // =========================================
  // SORTED LIST
  // =========================================

  const sortedList = [...filteredList].sort((a, b) => {
    if (!orderBy) return 0;

    let valueA = a[orderBy];

    let valueB = b[orderBy];

    if (typeof valueA === "string") valueA = valueA.toLowerCase();

    if (typeof valueB === "string") valueB = valueB.toLowerCase();

    if (valueA < valueB) return order === "asc" ? -1 : 1;

    if (valueA > valueB) return order === "asc" ? 1 : -1;

    return 0;
  });

  const renderHeader = (label, field) => (
    <TableCell>
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? order : "asc"}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      <Card
        sx={{
          mb: 2,
          p: 0,
          overflow: "hidden",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="alert-circle" width="18" />

          <Box sx={{ ml: 1 }}>
            {editId ? "Edit Sub Module" : "Add Sub Module"}
          </Box>
        </Box>

        <CardContent>
          <Grid container spacing={2}>
            {/* APPLICATION */}

            <Grid item lg={3}>
              <CustomFormLabel>Application</CustomFormLabel>

              <CustomTextField
                select
                fullWidth
                size="small"
                name="application_ID"
                value={formik.values.application_ID}
                onChange={(e) => {
                  formik.setFieldValue("application_ID", e.target.value);

                  formik.setFieldValue("module_ID", "");
                }}
                SelectProps={{
                  native: true,
                }}
                error={
                  formik.touched.application_ID &&
                  Boolean(formik.errors.application_ID)
                }
                helperText={
                  formik.touched.application_ID && formik.errors.application_ID
                }
              >
                <option value="">--Select Application--</option>

                {applications.map((item) => (
                  <option key={item.application_ID} value={item.application_ID}>
                    {item.application_Name}
                  </option>
                ))}
              </CustomTextField>
            </Grid>

            {/* MODULE */}

            <Grid item lg={3}>
              <CustomFormLabel>Module</CustomFormLabel>

              <CustomTextField
                select
                fullWidth
                size="small"
                name="module_ID"
                value={formik.values.module_ID}
                onChange={(e) => {
                  formik.setFieldValue("module_ID", e.target.value);
                }}
                SelectProps={{
                  native: true,
                }}
                error={
                  formik.touched.module_ID && Boolean(formik.errors.module_ID)
                }
                helperText={formik.touched.module_ID && formik.errors.module_ID}
              >
                <option value="">--Select Module--</option>

                {filteredModules.map((item) => (
                  <option key={item.module_ID} value={item.module_ID}>
                    {item.module_Name}
                  </option>
                ))}
              </CustomTextField>
            </Grid>

            {/* SUBMODULE */}

            <Grid item lg={3}>
              <CustomFormLabel>Sub Module Name</CustomFormLabel>

              <CustomTextField
                fullWidth
                size="small"
                placeholder="Enter Sub Module Name"
                name="sub_Module_Name"
                value={formik.values.sub_Module_Name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.sub_Module_Name &&
                  Boolean(formik.errors.sub_Module_Name)
                }
                helperText={
                  formik.touched.sub_Module_Name &&
                  formik.errors.sub_Module_Name
                }
              />
            </Grid>

            {/* STATUS */}

            <Grid item lg={1}>
              <CustomFormLabel>Status</CustomFormLabel>

              <Switch
                checked={formik.values.IsActive}
                onChange={(e) =>
                  formik.setFieldValue("IsActive", e.target.checked)
                }
              />
            </Grid>

            {/* BUTTON */}

            <Grid item lg={2} mt={4}>
              <Button
                variant="contained"
                onClick={formik.handleSubmit}
                disabled={!formik.isValid}
              >
                {editId ? "Update" : "Add"}
              </Button>

              <Button sx={{ ml: 2 }} variant="outlined" onClick={resetForm}>
                {editId ? "Cancel" : "Reset"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card
        sx={{
          mt: 2,
          p: 0,
          overflow: "hidden",
        }}
      >
        {/* HEADER */}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          {/* LEFT */}

          <Box display="flex" alignItems="center">
            <FeatherIcon icon="alert-circle" width="18" />

            <Box sx={{ ml: 1 }}>Existing Sub Modules</Box>
          </Box>

          {/* RIGHT */}

          <Box display="flex" alignItems="center">
            <TextField
              size="small"
              placeholder="Search..."
              value={globalSearch}
              onFocus={() => {
                setShowAllFilters(false);

                setSearchText({});
              }}
              onChange={(e) => {
                setGlobalSearch(e.target.value);

                setPage(0);
              }}
              sx={{
                width: 220,
                backgroundColor: "white",
                borderRadius: 1,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {globalSearch && (
              <CloseIcon
                sx={{
                  cursor: "pointer",
                  ml: 1,
                }}
                onClick={() => setGlobalSearch("")}
              />
            )}

            <FilterAltIcon
              sx={{
                cursor: "pointer",
                ml: 2,
                color: showAllFilters
                  ? (theme) => theme.palette.primary.main
                  : "text.primary",
                transition: "0.2s",
              }}
              onClick={() => setShowAllFilters(!showAllFilters)}
            />

            <Button
              variant="outlined"
              size="small"
              sx={{
                ml: 2,
                height: 32,
              }}
              onClick={handleResetFilters}
            >
              Reset Filter
            </Button>
          </Box>
        </Box>

        {/* TABLE */}

        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#FAFBFB",
                  }}
                >
                  {renderHeader("ID", "sub_Module_ID")}

                  {renderHeader("Application", "application_Name")}

                  {renderHeader("Module", "module_Name")}

                  {renderHeader("Sub Module", "sub_Module_Name")}

                  <TableCell>Status</TableCell>

                  <TableCell align="center">Action</TableCell>
                </TableRow>

                {showAllFilters && (
                  <TableRow>
                    {[
                      "sub_Module_ID",
                      "application_Name",
                      "module_Name",
                      "sub_Module_Name",
                    ].map((field) => (
                      <TableCell key={field}>
                        <TextField
                          size="small"
                          fullWidth
                          value={searchText[field] || ""}
                          onChange={(e) =>
                            setSearchText({
                              ...searchText,
                              [field]: e.target.value,
                            })
                          }
                          placeholder="Search..."
                          InputProps={{
                            endAdornment: searchText[field] && (
                              <InputAdornment position="end">
                                <CloseIcon
                                  fontSize="small"
                                  sx={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    setSearchText({
                                      ...searchText,
                                      [field]: "",
                                    })
                                  }
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                    ))}

                    <TableCell />

                    <TableCell />
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {sortedList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No sub modules found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {(rowsPerPage > 0
                  ? sortedList.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                  : sortedList
                ).map((item) => (
                  <TableRow key={item.sub_Module_ID}>
                    <TableCell>{item.sub_Module_ID}</TableCell>

                    <TableCell>{item.application_Name}</TableCell>

                    <TableCell>{item.module_Name}</TableCell>

                    <TableCell>{item.sub_Module_Name}</TableCell>

                    <TableCell>
                      <Tooltip title={item.IsActive ? "Active" : "Inactive"}>
                        <Switch
                          checked={item.IsActive}
                          size="small"
                          onChange={async (e) => {
                            try {
                              await UpdateApplicationsSubModules({
                                Sub_Module_ID: item.sub_Module_ID,

                                Module_ID: item.module_ID,

                                Sub_Module_Name: item.sub_Module_Name,

                                IsActive: e.target.checked,
                              });

                              toast.success("Status updated successfully");

                              fetchSubModules();
                            } catch {
                              toast.error("Failed to update status");
                            }
                          }}
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <EditIcon
                          sx={{
                            cursor: "pointer",
                            color: "primary.main",
                          }}
                          onClick={() => handleEdit(item)}
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={sortedList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));

              setPage(0);
            }}
            showFirstButton
            showLastButton
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddSubModules;
